import { Policy } from "@/types";
import type {
  CreatePolicyMutationVariables,
  DeletePolicyMutationVariables,
  UpdatePolicyMutationVariables,
} from "@/types/gql/graphql";
import {
  QUERY_CREATE_POLICY,
  QUERY_POLICIES,
  QUERY_REMOVE_POLICY,
  QUERY_UPDATE_POLICY,
} from "@/services/web/api-policy.const";
import { useContext, useMemo } from "react";
import { useTrackedMutation, useTrackedQuery } from "@/hooks/apollo-insights-hooks";

import { CityContext } from "@/contexts/city-context-provider";

export const usePolicies = () => {
  const { currentCity } = useContext(CityContext);

  const variables = useMemo(() => ({ cityId: currentCity?.id ?? "" }), [currentCity?.id]);

  const { data, loading } = useTrackedQuery(QUERY_POLICIES, {
    actionName: "QueryPolicies",
    variables,
    skip: !currentCity?.id,
  });

  const refetchPoliciesOptions = useMemo(
    () => ({
      refetchQueries: [{ query: QUERY_POLICIES, variables }],
      awaitRefetchQueries: true,
    }),
    [variables],
  );

  const [createNewPolicy] = useTrackedMutation(QUERY_CREATE_POLICY, {
    actionName: "CreatePolicy",
    ...refetchPoliciesOptions,
  });

  const [updatePolicy] = useTrackedMutation(QUERY_UPDATE_POLICY, {
    actionName: "UpdatePolicy",
    ...refetchPoliciesOptions,
  });

  const [deletePolicy] = useTrackedMutation(QUERY_REMOVE_POLICY, {
    actionName: "DeletePolicy",
    ...refetchPoliciesOptions,
  });

  const policyList = useMemo(() => (data?.policiesByCityId ?? []) as Policy[], [data]);

  const handleAddPolicy = async (variables: CreatePolicyMutationVariables) => {
    const response = await createNewPolicy({ variables });
    return response.data;
  };

  const handleUpdatePolicy = async (variables: UpdatePolicyMutationVariables) => {
    await updatePolicy({ variables });
  };

  const handleDeletePolicy = async (variables: DeletePolicyMutationVariables) => {
    await deletePolicy({ variables });
  };

  return {
    handleUpdatePolicy,
    handleAddPolicy,
    policyList,
    loading,
    handleDeletePolicy,
  };
};
